import logger from './logger';
/**
 * 案件管理と陸送員スケジュール関連の共通ヘルパー関数
 *
 * このファイルは、複数のページコンポーネントで重複していた
 * ビジネスロジックを統合し、一元管理することで保守性を向上させます。
 */

import type {
  Case,
  LandTransporter,
  BusinessPartner,
  BusinessPartnerLocation,
  VehicleModel,
  Manufacturer,
  ClientCompat,
} from '../types';
import { extractPrefecture } from './addressUtils';

/**
 * 車両名を取得する（company_casesテーブルのvehicle_display_nameを優先使用）
 * @param case_ 案件データ
 * @param getVehicleModelById 車両モデル取得関数（フォールバック用）
 * @param getManufacturerById メーカー取得関数（フォールバック用）
 * @returns 車両名（例：「トヨタ プリウス」）
 */
export const getVehicleName = (
  case_: Case,
  getVehicleModelById: (id: string) => VehicleModel | undefined,
  getManufacturerById: (id: string) => Manufacturer | undefined
): string => {
  logger.debug('🔍 getVehicleName開始 - 詳細診断', {
    caseId: case_.id,
    vehicleDisplayName: case_.vehicleDisplayName,
    vehicleModel: case_.vehicleModel,
    vehicleModelId: case_.vehicleModelId,
    vehicleModelIdType: typeof case_.vehicleModelId,
    hasVehicleModelId: !!case_.vehicleModelId,
  });

  // 1. company_casesテーブルから取得した完全な車両名を最優先で使用
  if (case_.vehicleDisplayName) {
    logger.debug('✅ getVehicleName: company_casesテーブルから車両名取得', {
      caseId: case_.id,
      vehicleDisplayName: case_.vehicleDisplayName,
    });
    return case_.vehicleDisplayName;
  }

  // 2. カスタム車両名の場合
  if (case_.vehicleModel === 'custom') {
    return (case_ as any).customVehicleName || '車両名不明';
  }

  // 3. フォールバック: 従来のロジック（マスターデータから検索）
  // vehicle_model_id (UUID) または vehicleModel (文字列) から検索
  const vehicleId = case_.vehicleModelId || case_.vehicleModel;
  logger.debug('🔍 getVehicleName: 車両ID検索開始 - 詳細診断', {
    caseId: case_.id,
    vehicleModelId: case_.vehicleModelId,
    vehicleModelIdType: typeof case_.vehicleModelId,
    vehicleModel: case_.vehicleModel,
    vehicleModelType: typeof case_.vehicleModel,
    vehicleId,
    vehicleIdType: typeof vehicleId,
    usedField: case_.vehicleModelId ? 'vehicleModelId' : 'vehicleModel',
  });

  if (vehicleId) {
    const model = getVehicleModelById(vehicleId);
    logger.debug('🔍 getVehicleName: モデル検索結果 - 詳細診断', {
      caseId: case_.id,
      searchedId: vehicleId,
      searchedIdType: typeof vehicleId,
      modelFound: !!model,
      model: model
        ? { id: model.id, name: model.name, manufacturerId: model.manufacturerId }
        : null,
      getVehicleModelByIdType: typeof getVehicleModelById,
      getVehicleModelByIdExists: !!getVehicleModelById,
    });

    if (model) {
      const manufacturer = getManufacturerById(model.manufacturerId);
      const vehicleName = manufacturer ? `${manufacturer.name} ${model.name}` : model.name;
      logger.debug('✅ getVehicleName: マスターデータから車両名取得', {
        caseId: case_.id,
        vehicleId,
        vehicleName,
      });
      return vehicleName;
    }

    // レガシーサポート: 直接文字列として扱う（vehicleModelが日本語の場合）
    if (
      typeof case_.vehicleModel === 'string' &&
      (case_.vehicleModel.match(/[\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FAF]/) ||
        case_.vehicleModel.includes(' '))
    ) {
      return case_.vehicleModel;
    }
  }

  logger.warn('⚠️ getVehicleName: 車両名を取得できませんでした', {
    caseId: case_.id,
    vehicleModelId: case_.vehicleModelId,
    vehicleModel: case_.vehicleModel,
  });
  return '車両名不明';
};

/**
 * 取引先名を取得する（拠点情報を含む）
 * @param partnerId 取引先ID
 * @param locationId 拠点ID（オプション）
 * @param getBusinessPartnerById 取引先取得関数
 * @param getBusinessPartnerLocationById 拠点取得関数
 * @returns 取引先名（拠点名を含む場合あり）
 */
export const getBusinessPartnerName = (
  partnerId: string,
  locationId: string | undefined,
  getBusinessPartnerById: (id: string) => BusinessPartner | undefined,
  getBusinessPartnerLocationById: (
    partnerId: string,
    locationId: string
  ) => BusinessPartnerLocation | undefined
): string => {
  const partner = getBusinessPartnerById(partnerId);
  if (!partner) return '不明';

  if (locationId) {
    const location = getBusinessPartnerLocationById(partnerId, locationId);
    if (location && !location.isDefault) {
      return `${partner.displayName || partner.name} ${location.name}`;
    }
  }

  return partner.displayName || partner.name;
};

/**
 * 役割別の取引先名を取得する（請求先、引き取り先、搬入先）
 * @param case_ 案件データ
 * @param role 役割（billing/pickup/delivery）
 * @param getBusinessPartnerById 取引先取得関数
 * @param getBusinessPartnerLocationById 拠点取得関数
 * @param clients 従来のクライアントデータ（後方互換性）
 * @returns 役割に応じた取引先名
 */
export const getPartnerNameByRole = (
  case_: Case,
  role: 'billing' | 'pickup' | 'delivery',
  getBusinessPartnerById: (id: string) => BusinessPartner | undefined,
  getBusinessPartnerLocationById: (
    partnerId: string,
    locationId: string
  ) => BusinessPartnerLocation | undefined,
  clients: ClientCompat[]
): string => {
  const partnerIdField = `${role}PartnerId` as keyof typeof case_;
  const locationIdField = `${role}LocationId` as keyof typeof case_;
  const legacyClientIdField = `${role}ClientId` as keyof typeof case_;

  const partnerId = case_[partnerIdField] as string;
  const locationId = case_[locationIdField] as string;
  const legacyClientId = case_[legacyClientIdField] as string;

  // 新しいBusinessPartnerフィールドが存在する場合はそれを使用
  if (partnerId) {
    return getBusinessPartnerName(
      partnerId,
      locationId,
      getBusinessPartnerById,
      getBusinessPartnerLocationById
    );
  }

  // 後方互換性のため、既存のclientIdも確認
  const client = clients.find(c => c.id === legacyClientId);
  return client?.companyName || '不明';
};

/**
 * 陸送員名を取得する
 * @param transporterId 陸送員ID
 * @param landTransporters 陸送員データ配列
 * @returns 陸送員名または状態メッセージ
 */
export const getTransporterName = (
  transporterId: string | undefined,
  landTransporters: LandTransporter[]
): string => {
  logger.info('🔍 getTransporterName呼び出し', {
    transporterId,
    landTransportersCount: landTransporters.length,
    landTransportersData: landTransporters.map(t => ({ id: t.id, name: t.name })),
  });

  if (!transporterId) return '未割当';

  // 陸送員データがまだ読み込まれていない場合の処理
  if (landTransporters.length === 0) {
    logger.info('⚠️ getTransporterName: 陸送員データが未読み込み', { transporterId });
    return '読み込み中...';
  }

  const transporter = landTransporters.find(t => t.id === transporterId);

  if (!transporter) {
    logger.info('⚠️ getTransporterName: 陸送員が見つかりません', {
      transporterId,
      availableTransporters: landTransporters.map(t => ({ id: t.id, name: t.name })),
    });
    return '不明な陸送員';
  }

  logger.info('✅ getTransporterName: 陸送員が見つかりました', {
    transporterId,
    transporterName: transporter.name,
  });

  return transporter.name;
};

/**
 * 案件状態のテキスト表現を取得する
 * @param status 案件状態
 * @returns 日本語の状態テキスト
 */
export const getCaseStatusText = (status: Case['status']): string => {
  switch (status) {
    case 'provisional':
      return '仮受';
    case 'confirmed':
      return '本受・未配車';
    case 'assigned':
      return '配車済み';
    case 'picked_up':
      return '引き取り済み';
    case 'in_transit':
      return '輸送中';
    case 'delivered':
      return '搬入済み';
    case 'reported':
      return '報告済み';
    case 'completed':
      return '完了';
    default:
      return '不明';
  }
};

/**
 * 案件状態の色クラスを取得する（Tailwind CSS用）
 * @param status 案件状態
 * @returns 状態に応じたCSSクラス
 */
export const getCaseStatusColor = (status: Case['status']): string => {
  switch (status) {
    case 'provisional':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
    case 'confirmed':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
    case 'assigned':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
    case 'picked_up':
      return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400';
    case 'in_transit':
      return 'bg-indigo-500 text-white dark:bg-indigo-600 dark:text-white';
    case 'delivered':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
    case 'reported':
      return 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400';
    case 'completed':
      return 'bg-green-600 text-white dark:bg-green-700 dark:text-white';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
};

/**
 * 陸送員スケジュール画面専用: 車両モデル名のみを取得（メーカー名を除外）
 *
 * 陸送員スケジュール画面では表示スペースが限られているため、
 * 「トヨタ クラウン」 → 「クラウン」のようにメーカー名を除外して表示します。
 *
 * @param case_ 案件データ
 * @param getVehicleModelById 車両モデル取得関数
 * @param getManufacturerById メーカー取得関数（通常の処理で使用）
 * @returns モデル名のみ（例：「クラウン」）
 *
 * @example
 * // 入力: vehicleDisplayName = "トヨタ クラウン"
 * // 出力: "クラウン"
 */
export const getVehicleNameForSchedule = (
  case_: Case,
  getVehicleModelById: (id: string) => VehicleModel | undefined,
  getManufacturerById: (id: string) => Manufacturer | undefined
): string => {
  // 1. 通常のgetVehicleNameで完全な車両名を取得
  const fullVehicleName = getVehicleName(case_, getVehicleModelById, getManufacturerById);

  // 2. メーカー名を除外してモデル名のみを抽出
  return extractModelNameOnly(fullVehicleName);
};

/**
 * スケジュール画面で使用する特殊な車両名定数
 * これらの値は後処理せずそのまま返す
 */
const SPECIAL_VEHICLE_NAMES = ['車両名不明', '読み込み中...'] as const;

/**
 * 車両名からメーカー名を除外し、モデル名のみを抽出
 *
 * 「メーカー名 モデル名」形式から、最後のスペース以降の文字列（モデル名）を抽出します。
 * エッジケース（スペースなし、特殊値など）にも対応しています。
 *
 * @param vehicleName 完全な車両名（例: "トヨタ クラウン"）
 * @returns モデル名のみ（例: "クラウン"）
 *
 * @example
 * extractModelNameOnly("トヨタ クラウン") // "クラウン"
 * extractModelNameOnly("Honda フィット") // "フィット"
 * extractModelNameOnly("クラウン") // "クラウン"
 * extractModelNameOnly("車両名不明") // "車両名不明"
 */
function extractModelNameOnly(vehicleName: string): string {
  // 特殊な値はそのまま返す
  if (
    !vehicleName ||
    SPECIAL_VEHICLE_NAMES.includes(vehicleName as (typeof SPECIAL_VEHICLE_NAMES)[number])
  ) {
    return vehicleName;
  }

  // 半角・全角スペースで分割し、最後の部分（モデル名）のみを取得
  const parts = vehicleName.split(/\s+/).filter(part => part.length > 0);

  if (parts.length === 0) {
    console.warn(`[extractModelNameOnly] Unexpected vehicle name format: "${vehicleName}"`);
    return vehicleName;
  }

  // 最後の部分をモデル名として返す
  return parts[parts.length - 1];
}

/**
 * 引取先の都道府県を取得する（FL-188: 陸送員スケジュール表示項目の拡充）
 * @param case_ 案件データ
 * @param getBusinessPartnerById 取引先取得関数
 * @param getBusinessPartnerLocationById 拠点取得関数
 * @returns 都道府県名（例: "埼玉県"）。抽出できない場合は"不明"
 */
export const getPickupPrefecture = (
  case_: Case,
  getBusinessPartnerById: (id: string) => BusinessPartner | undefined,
  getBusinessPartnerLocationById: (
    partnerId: string,
    locationId: string
  ) => BusinessPartnerLocation | undefined
): string => {
  const partnerId = case_.pickupPartnerId;
  const locationId = case_.pickupLocationId;

  if (!partnerId) {
    return '不明';
  }

  const partner = getBusinessPartnerById(partnerId);
  if (!partner) {
    return '不明';
  }

  // 拠点が指定されている場合、拠点の住所から都道府県を抽出
  if (locationId) {
    const location = getBusinessPartnerLocationById(partnerId, locationId);
    if (location?.address) {
      return extractPrefecture(location.address);
    }
  }

  // デフォルト拠点の住所から都道府県を抽出
  if (partner.defaultAddress) {
    return extractPrefecture(partner.defaultAddress);
  }

  return '不明';
};
